import { FC, useState } from 'react';
import { tailwindOverride } from '@getsynapse/design-system';
import { useElevation } from '@getsynapse/design-system';
import { AppSideNavProps } from './types';
import Header from './components/Header';
import Body from './components/Body';
import Footer from './components/Footer/Footer';

const AppSideNav: FC<AppSideNavProps> = ({
  itemGroups,
  renderItem,
  resourcesOptions,
  ...props
}) => {
  const elevation = useElevation(1);
  const [isExpanded, setIsExpanded] = useState<boolean>(true);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [isToggling, setIsToggling] = useState<boolean>(false);

  const handleToggleExpanded = () => {
    setIsToggling(true);
    setIsExpanded((prev) => {
      return !prev;
    });

    // Reset the toggling state after collapse animation completes (150ms based on duration-150 class)
    // This prevents Logo being in hovered state while the sidebar is collapsing.
    setTimeout(() => {
      setIsToggling(false);
    }, 150);
  };

  return (
    <div
      role='navigation'
      aria-label='Main navigation sidebar'
      className={tailwindOverride(
        'bg-primary',
        'flex',
        'flex-col',
        'px-2',
        'pt-4',
        'pb-2',
        'relative',
        'transition-width',
        'duration-150 ease-in',
        'motion-safe',
        'h-full',
        elevation,
        { 'w-44': isExpanded },
        { 'w-14': !isExpanded }
      )}
      onMouseEnter={() => {
        if (!isToggling) {
          setIsHovered(true);
        }
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <Header
        isExpanded={isExpanded}
        isHovered={isHovered && !isToggling}
        onToggleExpanded={handleToggleExpanded}
      />
      <Body
        itemGroups={itemGroups}
        renderItem={renderItem}
        isExpanded={isExpanded}
        {...props}
      ></Body>
      <Footer resourcesOptions={resourcesOptions} isExpanded={isExpanded} />
    </div>
  );
};

export default AppSideNav;
